import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, X, ChevronLeft, ChevronRight, Award, Download, ShieldCheck } from 'lucide-react';

const Thumb = ({ cert, size = 'sm', onClick }) => {
  const [errored, setErrored] = useState(false);
  const initial = (cert.issuer || cert.name || '?').replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase() || 'CR';
  const dim = size === 'sm' ? { w: 56, h: 40 } : { w: 96, h: 70 };
  if (cert.image && !errored) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="group relative rounded-md overflow-hidden border border-[#00f0ff]/30 bg-slate-950 transition-all hover:scale-105 hover:border-[#00f0ff] hover:shadow-[0_0_18px_rgba(0,240,255,0.45)] cursor-pointer"
        style={{ width: dim.w, height: dim.h }}
        title="Open certificate"
        data-testid={`cert-thumb-${cert.image.split('/').pop()}`}
      >
        <img
          src={cert.image}
          alt={cert.name}
          onError={() => setErrored(true)}
          className="w-full h-full object-cover bg-white"
          loading="lazy"
        />
        <span className="absolute inset-0 bg-gradient-to-t from-[#00f0ff]/0 group-hover:from-[#00f0ff]/15 transition-all" />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative rounded-md overflow-hidden border border-[#00f0ff]/30 bg-slate-950 transition-all hover:border-[#00f0ff] hover:shadow-[0_0_18px_rgba(0,240,255,0.4)] cursor-pointer flex items-center justify-center"
      style={{ width: dim.w, height: dim.h, background: 'linear-gradient(135deg, rgba(0,240,255,0.12), rgba(157,78,221,0.12))' }}
      title="Open certificate"
      data-testid={`cert-thumb-fallback-${initial}`}
    >
      <span className="font-display font-black text-[#00f0ff] text-sm tracking-tight">{initial}</span>
      <span className="absolute inset-0 bg-gradient-to-t from-[#00f0ff]/0 group-hover:from-[#00f0ff]/15 transition-all" />
    </button>
  );
};

const CertificateModal = ({ certs, index, onClose, onPrev, onNext }) => {
  const cert = certs[index];
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    setErrored(false);
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext, index]);

  if (!cert) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-4 pt-20 sm:pt-16 md:p-8 md:pt-20"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
      onClick={onClose}
      data-testid="cert-modal"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full border border-[#00f0ff]/40 bg-slate-950/70 text-[#00f0ff] hover:bg-[#00f0ff]/15 hover:scale-110 transition-all cursor-pointer flex items-center justify-center shadow-[0_0_18px_rgba(0,240,255,0.35)]"
        data-testid="cert-modal-prev"
        aria-label="Previous certificate"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full border border-[#00f0ff]/40 bg-slate-950/70 text-[#00f0ff] hover:bg-[#00f0ff]/15 hover:scale-110 transition-all cursor-pointer flex items-center justify-center shadow-[0_0_18px_rgba(0,240,255,0.35)]"
        data-testid="cert-modal-next"
        aria-label="Next certificate"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 220, damping: 24 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-5xl max-h-[85vh] sm:max-h-[88vh] flex flex-col rounded-2xl border border-[#00f0ff]/40 overflow-hidden"
        style={{ background: 'rgba(3,5,8,0.95)', boxShadow: '0 0 60px rgba(0,240,255,0.25), inset 0 0 0 1px rgba(0,240,255,0.08)' }}
      >
        <div className="px-5 py-4 border-b border-[#00f0ff]/15 bg-slate-950/80 flex items-start justify-between gap-3">
          <div className="flex gap-3 items-start min-w-0">
            <div className="w-9 h-9 rounded border border-[#00f0ff]/40 flex items-center justify-center text-[#00f0ff] bg-[#00f0ff]/5 shrink-0">
              <Award className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="font-mono text-[9px] text-[#00f0ff] tracking-widest uppercase">Credential</div>
              <h3 className="font-display font-black text-base md:text-lg text-white tracking-tight uppercase leading-tight line-clamp-2" data-testid="cert-modal-title">
                {cert.name}
              </h3>
              <p className="font-mono text-[10px] text-slate-400 mt-0.5 uppercase tracking-wide">{cert.issuer}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-colors shrink-0"
            data-testid="cert-modal-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto bg-slate-950/60 p-4 md:p-6 flex items-center justify-center" style={{ minHeight: 300 }}>
          {!errored && cert.image ? (
            <img
              src={cert.image}
              alt={cert.name}
              onError={() => setErrored(true)}
              className="rounded-md max-w-full max-h-[70vh] object-contain bg-white"
              style={{ boxShadow: '0 0 40px rgba(0,240,255,0.25)' }}
              data-testid="cert-modal-image"
            />
          ) : (
            <div
              className="rounded-md flex flex-col items-center justify-center text-center p-10 max-w-2xl"
              style={{ background: 'linear-gradient(135deg, rgba(0,240,255,0.08), rgba(157,78,221,0.06))', border: '1px solid rgba(0,240,255,0.25)', boxShadow: '0 0 40px rgba(0,240,255,0.2)' }}
              data-testid="cert-modal-placeholder"
            >
              <ShieldCheck className="w-12 h-12 text-[#00f0ff] mb-4" />
              <div className="font-display font-extrabold text-lg text-white">{cert.name}</div>
              <div className="font-mono text-[11px] text-slate-400 uppercase tracking-widest mt-2">{cert.issuer}</div>
              <div className="font-mono text-[9px] text-slate-500 mt-6 max-w-md">
                Certificate image not yet synced. Drop the file at
                <code className="mx-1 px-1.5 py-0.5 rounded bg-slate-900 border border-slate-800 text-[#00f0ff]">/app/frontend/public{cert.image || '/certificates/<file>.png'}</code>
                and refresh.
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[#00f0ff]/15 bg-slate-950/80 flex items-center justify-between gap-3">
          <div className="font-mono text-[10px] text-slate-500 tracking-widest uppercase">
            {index + 1} / {certs.length}
          </div>
          {cert.image && !errored && (
            <a
              href={cert.image}
              download
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#00f0ff]/40 bg-[#00f0ff]/10 text-[#00f0ff] hover:bg-[#00f0ff]/20 hover:scale-105 transition-all font-mono text-[10px] uppercase tracking-widest font-bold cursor-pointer"
              data-testid="cert-modal-download"
            >
              <Download className="w-3 h-3" />
              <span>Download Certificate</span>
            </a>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export const CertificateMatrix = ({ certifications }) => {
  const certs = certifications || [];
  const [openIdx, setOpenIdx] = useState(null);

  const open = useCallback((i) => setOpenIdx(i), []);
  const close = useCallback(() => setOpenIdx(null), []);
  const prev = useCallback(() => setOpenIdx((i) => (i === null ? 0 : (i - 1 + certs.length) % certs.length)), [certs.length]);
  const next = useCallback(() => setOpenIdx((i) => (i === null ? 0 : (i + 1) % certs.length)), [certs.length]);

  return (
    <section className="space-y-5" data-testid="credential-matrix">
      <style>{`
        /* Row pop-up + dim sibling rows */
        .cert-tbody:hover .cert-row { opacity: 0.45; filter: saturate(0.6); transition: all 280ms ease; }
        .cert-tbody .cert-row:hover {
          opacity: 1;
          filter: saturate(1.1);
          transform: scale(1.015);
          transform-origin: left center;
          z-index: 5;
          position: relative;
        }
        .cert-tbody .cert-row { transition: opacity 280ms ease, filter 280ms ease, transform 220ms ease; }
        .cert-tbody .cert-row:hover .cert-cell {
          background: linear-gradient(90deg, rgba(0,240,255,0.10) 0%, rgba(0,240,255,0.03) 60%, transparent 100%);
          border-bottom-color: rgba(0,240,255,0.45) !important;
          box-shadow: 0 8px 32px -8px rgba(0,240,255,0.35), 0 0 0 1px rgba(0,240,255,0.15) inset;
        }
        .cert-tbody .cert-row:hover .cert-cell:first-child {
          border-left: 3px solid #00f0ff;
          padding-left: calc(1rem - 3px);
        }
        @media (min-width: 768px) {
          .cert-tbody .cert-row:hover .cert-cell:first-child { padding-left: calc(1.5rem - 3px); }
        }
        /* Scan-line shimmer that sweeps once across the row on hover */
        .cert-tbody .cert-row:hover .cert-cell:first-child::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent 0%, rgba(0,240,255,0.18) 50%, transparent 100%);
          background-size: 200% 100%;
          animation: scan-sweep 900ms ease-out forwards;
          pointer-events: none;
        }
        .cert-tbody .cert-row .cert-cell:first-child { position: relative; overflow: hidden; }
        @keyframes scan-sweep {
          0% { background-position: -100% 0; opacity: 0; }
          20% { opacity: 1; }
          100% { background-position: 200% 0; opacity: 0; }
        }
        /* Index number pulse on hover */
        .cert-tbody .cert-row:hover .cert-index {
          text-shadow: 0 0 8px rgba(0,240,255,0.8);
          font-weight: 800;
        }
      `}</style>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded border border-[#00f0ff]/40 bg-[#00f0ff]/5 text-[#00f0ff] flex items-center justify-center">
            <Award className="w-4 h-4" />
          </div>
          <div>
            <h4 className="font-display font-black text-lg text-white uppercase tracking-tight leading-none">
              Credential Matrix <span className="text-[#00f0ff]">(Certs)</span>
            </h4>
            <p className="text-[11px] text-slate-500 font-mono mt-1 tracking-wide">
              A curated vault of verified professional certifications and achievements.
            </p>
          </div>
        </div>
        <div className="font-mono text-[10px] text-slate-500 uppercase tracking-widest border border-slate-800 bg-slate-950/60 px-2.5 py-1.5 rounded flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#39ff14] signal-dot" />
          <span>{certs.length} Verified</span>
        </div>
      </div>

      <div
        className="rounded-xl overflow-hidden relative"
        style={{
          background: 'linear-gradient(180deg, rgba(0,240,255,0.04) 0%, rgba(3,5,8,0.6) 100%)',
          border: '1px solid rgba(0,240,255,0.25)',
          boxShadow: '0 0 24px rgba(0,240,255,0.08), inset 0 0 0 1px rgba(0,240,255,0.05)',
        }}
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0,240,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,240,255,0.5) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="relative overflow-x-auto">
          <table className="w-full text-left border-separate" style={{ borderSpacing: 0 }}>
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-950/90 backdrop-blur">
                <th className="px-4 md:px-6 py-5 font-mono text-sm text-[#00f0ff] uppercase tracking-[0.18em] font-extrabold border-b border-[#00f0ff]/30" style={{ textShadow: '0 0 12px rgba(0,240,255,0.45)' }}>
                  Certification Name
                </th>
                <th className="px-4 md:px-6 py-5 font-mono text-sm text-[#00f0ff] uppercase tracking-[0.18em] font-extrabold border-b border-[#00f0ff]/30 hidden sm:table-cell" style={{ textShadow: '0 0 12px rgba(0,240,255,0.45)' }}>
                  Issuer
                </th>
                <th className="px-4 md:px-6 py-5 font-mono text-sm text-[#00f0ff] uppercase tracking-[0.18em] font-extrabold w-[140px] text-center border-b border-[#00f0ff]/30">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-[#00f0ff]/40 bg-[#00f0ff]/5" style={{ boxShadow: '0 0 14px rgba(0,240,255,0.25), inset 0 0 8px rgba(0,240,255,0.15)' }}>
                    <Eye className="w-4 h-4" />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="cert-tbody">
              {certs.map((cert, idx) => (
                <motion.tr
                  key={cert.name}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35, delay: idx * 0.04, ease: 'easeOut' }}
                  className="cert-row group cursor-pointer"
                  onClick={() => open(idx)}
                  data-testid={`cert-row-${idx}`}
                >
                  <td className="cert-cell pl-4 md:pl-6 pr-3 py-4 align-middle border-b border-slate-900/60">
                    <div className="flex items-start gap-3">
                      <span className="cert-index font-mono text-[11px] text-slate-600 mt-1 shrink-0 transition-all duration-300 group-hover:text-[#00f0ff]">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <div className="min-w-0">
                        <div className="font-display font-bold text-[14px] text-slate-100 leading-snug transition-colors duration-200 group-hover:text-white">
                          {cert.name}
                        </div>
                        <div className="sm:hidden font-mono text-[10px] text-slate-500 uppercase tracking-wide mt-1">
                          {cert.issuer}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="cert-cell px-4 md:px-6 py-4 align-middle font-mono text-[12px] text-slate-300 hidden sm:table-cell border-b border-slate-900/60 transition-colors duration-200 group-hover:text-slate-100">
                    {cert.issuer}
                  </td>
                  <td className="cert-cell px-4 md:px-6 py-4 align-middle border-b border-slate-900/60">
                    <div className="relative flex items-center justify-center">
                      <Thumb cert={cert} onClick={(e) => { e?.stopPropagation?.(); open(idx); }} />
                      <Eye className="absolute -right-2 md:-right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#00f0ff] transition-all duration-300 opacity-0 group-hover:opacity-100 group-hover:-right-5 md:group-hover:-right-6 pointer-events-none" />
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AnimatePresence>
        {openIdx !== null && (
          <CertificateModal
            certs={certs}
            index={openIdx}
            onClose={close}
            onPrev={prev}
            onNext={next}
          />
        )}
      </AnimatePresence>
    </section>
  );
};
